import {
  buildReviewReceipt,
  type CodeMapCompilation,
  type CoverageState,
} from "../../shared/codemap";
import type { ChewConfig } from "../../shared/config";
import type { PrimaryNature, ReviewGuide } from "../../shared/guide";
import type { ProgressProjection } from "../../shared/progress";

export const natureLabels: Record<PrimaryNature, string> = {
  authorization: "Authorization",
  "business-logic": "Business logic",
  "controller-entrypoint": "Controllers",
  contract: "Contracts",
  "data-persistence": "Data and persistence",
  integration: "Integrations",
  "tests-proof": "Tests and proof",
  generated: "Generated",
  "wiring-config": "Wiring and config",
  frontend: "Frontend",
  infrastructure: "Infrastructure",
  "analytics-data-model": "Analytics and data models",
  other: "Other",
};

const stateLabels: Record<CoverageState, string> = {
  "directly-presented": "Direct",
  "structurally-summarized": "Summarized",
  "intentionally-skimmed": "Skimmed",
  "excluded-with-reason": "Excluded",
  unseen: "Unseen",
  stale: "Stale",
};

function percent(part: number, total: number) {
  return total === 0 ? 0 : Math.round((part / total) * 100);
}

function NatureBar({
  states,
  total,
}: {
  states: Record<CoverageState, number>;
  total: number;
}) {
  return (
    <div aria-label="Coverage breakdown" className="coverage-bar">
      {(Object.keys(stateLabels) as CoverageState[]).map((state) =>
        states[state] > 0 ? (
          <span
            className={`coverage-segment coverage-${state}`}
            key={state}
            style={{ width: `${percent(states[state], total)}%` }}
            title={`${stateLabels[state]}: ${states[state]} changed LOC`}
          />
        ) : null,
      )}
    </div>
  );
}

export function CoveragePlan({
  compilation,
  guide,
}: {
  compilation: CodeMapCompilation;
  guide: ReviewGuide;
}) {
  const grouped = new Map<PrimaryNature, { loc: number; direct: number; summary: number; skim: number }>();
  for (const region of compilation.regions) {
    const group = grouped.get(region.nature) ?? { loc: 0, direct: 0, summary: 0, skim: 0 };
    const loc = region.changedLoc ?? 0;
    group.loc += loc;
    if (region.coverageMode === "direct") group.direct += loc;
    if (region.coverageMode === "structured-summary") group.summary += loc;
    if (region.coverageMode === "intentional-skim") group.skim += loc;
    grouped.set(region.nature, group);
  }
  const categories = [...grouped.entries()].sort((left, right) => right[1].loc - left[1].loc);

  return (
    <section aria-labelledby="coverage-plan-title" className="coverage-card coverage-plan-card">
      <div className="coverage-heading">
        <div>
          <span className="quiet-label">Codemap</span>
          <h2 id="coverage-plan-title">What this walkthrough accounts for</h2>
        </div>
        <span>{compilation.summary.verifiedFiles}/{guide.coverage.expectedFiles} files source-verified</span>
      </div>
      <p>
        Code types are classified independently from tour order. High-risk regions receive direct focal stops;
        boilerplate and proof can use structured summaries.
      </p>
      <div className="coverage-plan-rows">
        {categories.map(([nature, group]) => (
          <div className="coverage-plan-row" key={nature}>
            <strong>{natureLabels[nature]}</strong>
            <div className="coverage-plan-bar">
              {group.direct > 0 && <span className="plan-direct" style={{ width: `${percent(group.direct, group.loc)}%` }} />}
              {group.summary > 0 && <span className="plan-summary" style={{ width: `${percent(group.summary, group.loc)}%` }} />}
              {group.skim > 0 && <span className="plan-skim" style={{ width: `${percent(group.skim, group.loc)}%` }} />}
            </div>
            <span>{group.loc} LOC</span>
          </div>
        ))}
      </div>
      <div className="coverage-legend" aria-label="Planned presentation legend">
        <span><i className="plan-direct" />Direct</span>
        <span><i className="plan-summary" />Structured summary</span>
        <span><i className="plan-skim" />Intentional skim</span>
      </div>
      {compilation.summary.unverifiedFiles > 0 && (
        <div className="coverage-warning">
          <strong>Prototype source limit</strong>
          <span>
            {compilation.summary.unverifiedFiles} changed files are absent from this illustrative fixture.
            CHEW will leave them unverified in the receipt rather than infer coverage.
          </span>
        </div>
      )}
    </section>
  );
}

export function ReviewReceiptView({
  compilation,
  compact = false,
  config,
  guide,
  progress,
}: {
  compilation: CodeMapCompilation;
  compact?: boolean;
  config: ChewConfig;
  guide: ReviewGuide;
  progress: ProgressProjection;
}) {
  const receipt = buildReviewReceipt(guide, compilation, progress, config);
  const authorization = receipt.categories.find((category) => category.nature === "authorization");
  const authorizationDirect = authorization
    ? percent(authorization.states["directly-presented"], authorization.changedLoc)
    : undefined;
  const exact = receipt.unverifiedFiles === 0;
  const ReceiptHeading = compact ? "h2" : "h1";

  return (
    <section aria-labelledby="review-receipt-title" className={`receipt-card${compact ? " receipt-card-compact" : ""}`}>
      <span className="quiet-label">Review Receipt</span>
      <ReceiptHeading id="review-receipt-title">What CHEW presented</ReceiptHeading>
      <p className="receipt-lead">
        {exact
          ? `All changed code is classified in the codemap. This review directly presented ${receipt.directProductionPercent}% of production-code LOC${authorizationDirect === undefined ? "." : `, including ${authorizationDirect}% of authorization LOC.`}`
          : `${compilation.summary.verifiedFiles} of ${guide.coverage.expectedFiles} changed files were verified from bundled source. The remaining ${receipt.unverifiedFiles} files are left source-unverified and are not counted as reviewed.`}
      </p>
      <details className="receipt-method">
        <summary>How coverage is measured</summary>
        <p>
          CHEW records what it brought into focus and which tour parts you advanced through. It does not claim to know where you looked.
          Code types excluded from the effective diff do not inflate the direct-review percentage.
        </p>
      </details>

      <div className="receipt-categories">
        {receipt.categories.map((category) => {
          const accounted =
            category.states["directly-presented"] +
            category.states["structurally-summarized"] +
            category.states["intentionally-skimmed"] +
            category.states["excluded-with-reason"];
          return (
            <details className="receipt-category" key={category.nature}>
              <summary>
                <div>
                  <strong>{natureLabels[category.nature]}</strong>
                  <span>{percent(accounted, category.changedLoc)}% review-covered · {category.changedLoc} changed LOC</span>
                </div>
                <NatureBar states={category.states} total={category.changedLoc} />
              </summary>
              <div className="receipt-region-list">
                {category.regions.map((region) => (
                  <div key={region.id}>
                    <span className={`receipt-state coverage-${region.coverageState}`}>
                      {stateLabels[region.coverageState]}
                    </span>
                    <div>
                      <strong>{region.path.split("/").at(-1)}</strong>
                      <small>{region.path}</small>
                    </div>
                    <span>{region.changedLoc ?? "?"} LOC · {region.scrutiny}</span>
                  </div>
                ))}
              </div>
            </details>
          );
        })}
      </div>

      {compilation.unverifiedPaths.length > 0 && (
        <details className="unverified-audit">
          <summary>{compilation.unverifiedPaths.length} source-unverified files</summary>
          <div>
            {compilation.unverifiedPaths.slice(0, 20).map((path) => <code key={path}>{path}</code>)}
            {compilation.unverifiedPaths.length > 20 && (
              <span>…and {compilation.unverifiedPaths.length - 20} more in the exported receipt.</span>
            )}
          </div>
        </details>
      )}
    </section>
  );
}
